<script setup>
import {computed} from 'vue'
import {useViewStore} from '@/stores/view'
import SearchFilterFieldInternalLink from './SearchFilterFieldInternalLink.vue'
import SearchFilterFieldSelect from './SearchFilterFieldSelect.vue'
import SearchFilterFieldCategory from './SearchFilterFieldCategory.vue'

const props = defineProps({
    viewId: String,
    filterKey: Number
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Static options definitions
const checkboxOptions = [
    {text: 'Select…'},
    {value: 1, text: 'Checked'},
    {value: 0, text: 'Not checked'}
]

const passwordOptions = [
    {text: 'Select…'},
    {value: 'is set', text: 'Set'},
    {value: 'is not set', text: 'Not set'}
]

const filter = computed(() => {
    return viewStore.filters?.[props.filterKey] || null
})

const value = computed({
    get() {
        if (!filter.value) return ''
        let val = filter.value.value

        // Safeguarded parsing for query strings
        if (filter.value.type === 'relationshipOneToMany' && val !== undefined && val !== '') {
            val = parseInt(val) || 0
        }

        return val
    },
    set(val) {
        if (!filter.value) return
        viewStore.setFilterProp({
            key: props.filterKey,
            name: 'value',
            value: val
        })
    }
})

const isActiveRelationshipField = computed(() => {
    if (!filter.value) return false

    if (
        filter.value.type === 'relationshipOneToMany'
        && (
            filter.value.condition === 'is associated to'
            || filter.value.condition === 'is not associated to'
        )
    ) {
        return true
    }

    if (
        filter.value.type === 'relationshipManyToMany'
        && (
            filter.value.condition === 'is associated to many'
            || filter.value.condition === 'is not associated to many'
        )
    ) {
        return true
    }

    return false
})

const isActive = (types) => {
    if (!filter.value) return false

    return types.indexOf(filter.value.type) > -1
        && filter.value.condition !== 'is set'
        && filter.value.condition !== 'is not set'
}

const closeOffcanvas = () => {
    const offcanvas = document.getElementById('offcanvasFilters')
    if (offcanvas) {
        offcanvas.querySelector('[data-bs-dismiss="offcanvas"]')?.click()
    }
}
</script>

<template>
    <div>
        <!-- Wrapped elements don't require changes, but methods are now fully guarded against undefined filters -->
        <input v-if="isActive(['currency', 'email', 'tel', 'text', 'textarea', 'texteditor', 'url'])"
               @keyup.enter="closeOffcanvas" v-model="value" class="form-control form-control-sm"/>

        <input v-else-if="isActive(['number'])" @keyup.enter="closeOffcanvas" type="number" v-model="value"
               class="form-control form-control-sm w-auto"/>

        <select v-else-if="isActive(['checkbox'])" v-model="value" class="form-select form-select-sm w-auto">
            <option v-for="option in checkboxOptions" :value="option.value" :key="option.text">
                {{ option.text }}
            </option>
        </select>

        <input v-else-if="isActive(['color'])" v-model="value" class="form-control form-control-sm" maxlength="7"
               minlength="7" placeholder="#000000" size="8">

        <input v-else-if="isActive(['date'])" v-model="value" type="datetime-local" class="form-control w-auto">

        <input v-else-if="isActive(['dateDate'])" v-model="value" type="date" class="form-control form-control-sm">

        <input v-else-if="isActive(['datetime-local'])" v-model="value" type="datetime-local"
               class="form-control form-select-sm">

        <SearchFilterFieldInternalLink v-else-if="isActive(['internalLink'])" :view-id="viewId" :filter-key="filterKey"
                                       style="display: flex"/>

        <select v-else-if="isActive(['password'])" v-model="value" class="form-select form-select-sm">
            <option v-for="option in passwordOptions" :value="option.value" :key="option.text">
                {{ option.text }}
            </option>
        </select>

        <input v-else-if="isActive(['range'])" v-model="value" class="form-control form-control-sm" type="number"/>

        <SearchFilterFieldCategory v-else-if="isActiveRelationshipField" v-model="value" :view-id="viewId"
                                   :filter-key="filterKey"/>

        <SearchFilterFieldSelect v-else-if="isActive(['select'])" :view-id="viewId" :filter-key="filterKey"/>

        <input v-else-if="isActive(['time'])" v-model="value" class="form-control form-control-sm" type="time">
    </div>
</template>